export interface Department {
  id: string
  name: string
}

export interface TeamMember {
  id: string
  name: string
  title: string
  department: string
  email: string
  phone: string
  location: string
  photo: string
  specialties: string[]
  education?: string
  school?: string
  previousEmployer?: string
  linkedin?: string
  twitter?: string
  github?: string
  hireDate?: string
  employeeId?: string
  notes?: string
  bio?: string
  affiliations?: string[]
}

export const departments: Department[] = [
  { id: 'all', name: 'All Departments' },
  { id: 'executive', name: 'Executive Leadership' },
  { id: 'consulting', name: 'Consulting Services' },
  { id: 'business-development', name: 'Business Development' },
  { id: 'operations', name: 'Operations' },
  { id: 'finance', name: 'Finance & Administration' },
  { id: 'legal', name: 'Legal & Compliance' },
  { id: 'marketing', name: 'Marketing & Communications' },
  { id: 'technology', name: 'Technology & Analytics' }
]

export const departmentName = (id: string): string =>
  departments.find(dept => dept.id === id)?.name ?? id

export const employees: TeamMember[] = [
  {
    id: 'alexandra-chen',
    name: 'Dr. Alexandra Chen',
    title: 'Chief Executive Officer',
    department: 'executive',
    email: 'a.chen@nexusglobal.com',
    phone: '+1-555-639-8701',
    location: 'Valdoria City HQ',
    photo: '/images/team/alexandra-chen.png',
    specialties: ['Strategic Planning', 'International Markets', 'Executive Leadership'],
    education: 'PhD, International Economics - Harvard University',
    previousEmployer: 'McKinsey & Company',
    linkedin: 'alexandra-chen-nexus',
    twitter: '@achen_nexus',
    bio: 'With over 15 years in international business development, Dr. Chen has led Nexus Global to become a premier consulting firm. Before founding Nexus she served as VP of Global Strategy at McKinsey & Company. She is a member of the International Business Consultants Association (IBCA) and delivered the keynote at the Global Markets Summit 2023.',
    affiliations: ['International Business Consultants Association (IBCA)', 'Global Markets Summit 2023 - Keynote Speaker']
  },
  {
    id: 'michael-rodriguez',
    name: 'Michael Rodriguez',
    title: 'Chief Operating Officer',
    department: 'executive',
    email: 'm.rodriguez@nexusglobal.com',
    phone: '+1-555-639-8702',
    location: 'Valdoria City HQ',
    photo: '/images/team/michael-rodriguez.png',
    specialties: ['Operations Management', 'Process Optimization', 'Team Leadership'],
    education: 'MBA - INSEAD',
    previousEmployer: 'Deloitte Consulting',
    linkedin: 'michael-rodriguez-ops',
    twitter: '@mrodriguez_ops',
    bio: 'Michael brings over 12 years of operational excellence and process optimization experience to Nexus Global. A former Director of Operations at Deloitte Consulting, his leadership in streamlining international operations has significantly improved our service delivery and client satisfaction.',
    affiliations: ['Project Management Institute (PMP)']
  },
  {
    id: 'elena-volkov',
    name: 'Elena Volkov',
    title: 'Chief Strategy Officer',
    department: 'executive',
    email: 'e.volkov@nexusglobal.com',
    phone: '+1-555-639-8703',
    location: 'Valdoria City HQ',
    photo: '/images/team/elena-volkov.png',
    specialties: ['Strategic Analysis', 'Market Research', 'Competitive Intelligence'],
    education: 'MSc Economics - London School of Economics',
    previousEmployer: 'Boston Consulting Group',
    linkedin: 'elena-volkov-strategy',
    bio: 'Elena is a strategic mastermind with extensive experience in market analysis and competitive intelligence. A former Senior Partner at Boston Consulting Group, her insights have guided numerous successful market entry strategies across emerging markets. She served as a panelist at the Global Markets Summit 2023.',
    affiliations: ['IBCA Member', 'Global Markets Summit 2023 - Panelist']
  },
  {
    id: 'katie-david',
    name: 'Katie David',
    title: 'Chief Financial Officer',
    department: 'executive',
    email: 'k.david@nexusglobal.com',
    phone: '+1-555-639-8704',
    location: 'Valdoria City HQ',
    photo: '/images/team/katie-david.png',
    specialties: ['Financial Planning', 'Investment Strategy', 'Risk Management'],
    education: 'MBA - Wharton School',
    previousEmployer: 'PwC Advisory Services',
    linkedin: 'katie-david-cfo',
    bio: 'Katie oversees all financial operations and strategic investment decisions at Nexus Global. Previously a Senior Manager at PwC Advisory Services, her expertise in international finance and risk management was central to our 2022 Series C round led by Harbor Point Capital.',
    affiliations: ['Certified Public Accountant (CPA)']
  },
  {
    id: 'scott-johnson',
    name: 'Scott Johnson',
    title: 'Senior Business Consultant',
    department: 'consulting',
    email: 's.johnson@nexusglobal.com',
    phone: '+1-555-639-8710',
    location: 'Valdoria City HQ',
    photo: '/images/team/scott-johnson.png',
    specialties: ['Market Entry', 'Strategic Partnerships', 'Due Diligence'],
    education: 'BBA - Valdoria State University',
    previousEmployer: 'TechVenture Partners',
    linkedin: 'scott-johnson-consulting',
    bio: 'Scott specializes in helping companies navigate complex international markets and establish strategic partnerships. Before joining Nexus he was an engagement manager at TechVenture Partners. His expertise in due diligence and market entry has facilitated over 50 successful business expansions.'
  },
  {
    id: 'robert-martinez',
    name: 'Robert Martinez',
    title: 'International Markets Director',
    department: 'business-development',
    email: 'r.martinez@nexusglobal.com',
    phone: '+1-555-639-8715',
    location: 'Astoria Regional Office',
    photo: '/images/team/robert-martinez.png',
    specialties: ['Emerging Markets', 'Partnership Development', 'Cross-Cultural Business'],
    education: 'MA International Relations - Georgetown University',
    previousEmployer: 'HSBC Global Banking',
    linkedin: 'robert-martinez-intl',
    bio: 'Robert leads our international market development initiatives with a focus on emerging economies. A veteran of HSBC Global Banking, his deep understanding of cross-cultural business practices has opened doors to new opportunities across three continents.'
  },
  {
    id: 'lisa-wang',
    name: 'Lisa Wang',
    title: 'Senior Financial Analyst',
    department: 'finance',
    email: 'l.wang@nexusglobal.com',
    phone: '+1-555-639-8720',
    location: 'Valdoria City HQ',
    photo: '/images/team/lisa-wang.png',
    specialties: ['Financial Modeling', 'Investment Analysis', 'Market Valuation'],
    education: 'BSc Finance - Valdoria Institute of Technology',
    previousEmployer: 'Astoria Energy Corp',
    linkedin: 'lisa-wang-finance',
    bio: 'Lisa builds the financial models that guide investment decisions at Nexus Global. A graduate of the Valdoria Institute of Technology, she previously worked in corporate finance at Astoria Energy Corp before joining the Nexus finance team.'
  },
  {
    id: 'jenny-thompson',
    name: 'Jenny Thompson',
    title: 'Operations Manager',
    department: 'operations',
    email: 'j.thompson@nexusglobal.com',
    phone: '+1-555-639-8725',
    location: 'Valdoria City HQ',
    photo: '/images/team/jenny-thompson.png',
    specialties: ['Project Management', 'Quality Assurance', 'Process Improvement'],
    bio: 'Jenny keeps day-to-day operations at Nexus Global running smoothly, coordinating project delivery across departments. She champions quality assurance and process improvement initiatives that keep client engagements on time and on budget.'
  },
  {
    id: 'maria-garcia',
    name: 'Maria Garcia',
    title: 'Legal Counsel',
    department: 'legal',
    email: 'm.garcia@nexusglobal.com',
    phone: '+1-555-639-8730',
    location: 'Valdoria City HQ',
    photo: '/images/team/maria-garcia.png',
    specialties: ['International Law', 'Contract Negotiation', 'Regulatory Compliance'],
    bio: 'Maria advises Nexus Global on international law, contract negotiation, and regulatory compliance across the markets the firm operates in. She ensures every client engagement meets the highest legal and ethical standards.'
  },
  {
    id: 'tammy-brown',
    name: 'Tammy Brown',
    title: 'Marketing Director',
    department: 'marketing',
    email: 't.brown@nexusglobal.com',
    phone: '+1-555-639-8735',
    location: 'Astoria Regional Office',
    photo: '/images/team/tammy-brown.png',
    specialties: ['Digital Marketing', 'Brand Strategy', 'Content Development'],
    bio: 'Tammy leads brand strategy and marketing communications for Nexus Global from the Astoria Regional Office. Her work spans digital marketing campaigns, content development, and building the firm\u2019s presence across international markets.'
  },
  {
    id: 'john-david',
    name: 'John David',
    title: 'Technology Analyst',
    department: 'technology',
    email: 'j.david@nexusglobal.com',
    phone: '+1-555-639-8740',
    location: 'Valdoria City HQ',
    photo: '/images/team/john-david.png',
    specialties: ['Data Analytics', 'Business Intelligence', 'System Integration'],
    bio: 'John turns data into insight for the Nexus Global consulting teams, building business intelligence dashboards and supporting system integration projects that help clients make better decisions.'
  },
  {
    id: 'carlos-lopez',
    name: 'Carlos Lopez',
    title: 'Business Development Manager',
    department: 'business-development',
    email: 'c.lopez@nexusglobal.com',
    phone: '+1-555-639-8745',
    location: 'Astoria Regional Office',
    photo: '/images/team/robert-martinez.png',
    specialties: ['Client Relations', 'Sales Strategy', 'Market Expansion'],
    bio: 'Carlos develops new client relationships and drives market expansion from the Astoria Regional Office. He partners closely with the international markets team to identify and win new opportunities.'
  },
  {
    id: 'david-park',
    name: 'David Park',
    title: 'Senior Backend Developer',
    department: 'technology',
    email: 'd.park@nexusglobal.com',
    phone: '+1-555-639-8742',
    location: 'Remote (Astoria)',
    photo: '/images/team/scott-johnson.png',
    specialties: ['API Development', 'Database Architecture', 'Retro Gaming'],
    education: 'BSc Computer Science - Valdoria Institute of Technology',
    previousEmployer: 'CloudServe Pro',
    github: 'dpark-dev',
    linkedin: 'david-park-dev',
    bio: "David architects the systems that keep Nexus running behind the scenes. A graduate of the Valdoria Institute of Technology and a former engineer at CloudServe Pro, he is a lifelong retro-gamer and famously evangelical about the original 1987 run-and-gun classic Contra - ask him about it and he will happily recite its legendary 30-lives cheat code from memory. David firmly believes every great piece of software deserves a hidden easter egg, and word around the office is that he snuck one into this very website. Up for the challenge?",
    affiliations: ['Retro Gaming Society']
  },
  // ============================================================
  // OSINT EASTER EGGS - Suspicious employees with hidden clues
  // ============================================================
  {
    id: 'victor-petrov',
    name: 'Victor Petrov',
    title: 'Senior Consultant - Special Projects',
    department: 'consulting',
    email: 'v.petrov@nexusglobal.com',
    phone: '+1-555-639-8750',
    location: 'Valdoria City HQ',
    photo: '/images/team/john-david.png',
    specialties: ['Government Relations', 'Risk Assessment', 'Confidential Projects'],
    // CLUE: Hire date is 2016, but company was founded in 2018!
    hireDate: '2016-03-15',
    // Hidden social media - students can search for these
    linkedin: 'linkedin.com/in/v-petrov-consulting',
    twitter: '@vpetrov_nexus',
    bio: 'Victor handles special projects and government relations for Nexus Global. His engagements are largely confidential, and colleagues note he has been with the firm "since the very beginning" - his personnel file lists a hire date of March 2016.'
  },
  {
    id: 'sarah-blackwood',
    name: 'Sarah Blackwood',
    title: 'Director of Internal Affairs',
    department: 'operations',
    email: 's.blackwood@nexusglobal.com',
    // CLUE: Phone extension 0000 is suspicious
    phone: '+1-555-639-0000',
    // CLUE: This office location doesn't exist on the website
    location: 'Valdoria City - Annex B',
    photo: '/images/team/jenny-thompson.png',
    specialties: ['Internal Investigations', 'Security Clearance', 'Personnel Vetting'],
    // CLUE: Employee ID format different from others
    employeeId: 'NGS-CLASSIFIED-001',
    bio: 'Sarah leads internal investigations and personnel vetting from the Valdoria City Annex B facility. Her role covers security clearance reviews and other sensitive internal matters that are not publicly detailed.'
  },
  {
    id: 'james-chen-it',
    name: 'James Chen',
    title: 'IT Security Administrator',
    department: 'technology',
    // CLUE: Different email domain
    email: 'j.chen@nexusglobal-consulting.com',
    // CLUE: Internal extension format
    phone: '+1-555-0199 ext. 4421',
    location: 'Valdoria City HQ - Server Room',
    photo: '/images/team/scott-johnson.png',
    specialties: ['Network Security', 'Access Control', 'Incident Response'],
    // CLUE: Direct admin access hint
    notes: 'Contact for all system access requests',
    bio: 'James administers network security and access control from the Valdoria City HQ server room. He is the primary point of contact for all system access requests and incident response.'
  },
  {
    id: 'ghost-employee',
    name: 'Marcus Webb',
    title: 'Chief Technology Officer',
    department: 'executive',
    email: 'cto@nexusglobal.com',
    phone: '+1-555-639-8705',
    // CLUE: Listed as HQ but also mentioned in humans.txt as remote
    location: 'Valdoria City HQ',
    photo: '/images/team/michael-rodriguez.png',
    specialties: ['Technology Strategy', 'Digital Transformation', 'System Architecture'],
    // CLUE: Former employer is now a "strategic partner" (revolving door)
    education: 'MS Computer Engineering - Stanford University',
    previousEmployer: 'Meridian Consulting',
    // CLUE: Not listed on Leadership page - why?
    linkedin: 'marcus-webb-nexus',
    twitter: '@marcuswebb_tech',
    bio: 'Marcus leads engineering and platform strategy at Nexus Global. He joined in 2021 as a Principal from Meridian Consulting - the same boutique advisory firm that Nexus now lists among its strategic partners. Marcus focuses on scaling our data platform and secure client infrastructure.',
    affiliations: ['IBCA Member']
  },
  {
    id: 'former-employee',
    name: 'Kevin Zhao',
    title: 'Technology Intern',
    department: 'technology',
    // CLUE: Matches the intern from humans.txt
    email: 'k.zhao@nexusglobal-consulting.com',
    phone: '+1-555-639-8799',
    location: 'Valdoria City HQ',
    photo: '/images/team/john-david.png',
    specialties: ['Frontend Development', 'Data Entry', 'Documentation'],
    // CLUE: Mentioned in security.txt as bugbounty contact
    school: 'Valdoria Institute of Technology',
    github: 'github.com/kzhao-intern',
    education: 'BSc Computer Science (in progress) - Valdoria Institute of Technology',
    bio: 'Kevin supports the technology team with frontend development and documentation. He is currently completing his degree at the Valdoria Institute of Technology - the same school attended by several members of the Nexus team - and is our current campus-recruiting intern.'
  }
]

export const getEmployeeById = (id?: string): TeamMember | undefined =>
  employees.find(emp => emp.id === id)
